/**
 * Black Forest Flowers — delivery details validation.
 *
 * Capture is native: the inputs rendered by snippets/bff-delivery-fields.liquid post with the
 * add-to-cart form (via the HTML5 `form=` attribute) or with the cart form. This file only adds
 * ENFORCEMENT on top of that — it is not required for the data to reach the order.
 *
 * Listeners are registered at the DOCUMENT in the CAPTURE phase so they run before Minimog's own
 * at-target listeners on the form (registration order would otherwise put us second, and the theme's
 * ProductForm would have already fired). This also lets us cover two paths Minimog's own
 * validateForm() misses:
 *   - the sticky add-to-cart bar, whose form is outside `.m-main-product--info`, so the theme's
 *     validateForm(null) returns [] and waves it straight through;
 *   - the Sunday rule, which the theme knows nothing about.
 */
(function () {
  'use strict';

  var SUNDAY = 0;
  var SUNDAY_MESSAGE =
    'We do not deliver on Sundays. Please choose another day — Sunday orders are delivered on Monday.';
  var PAST_MESSAGE = 'Please choose today or a later date.';
  var REQUIRED_MESSAGE = 'This field is required.';

  /**
   * Parse a YYYY-MM-DD value as a LOCAL date.
   * `new Date('2026-07-12')` is parsed as UTC midnight, which in a negative-offset timezone
   * (the shop is America/New_York) resolves to the PREVIOUS day locally — that would shift
   * getDay() by one and reject Mondays while allowing Sundays.
   */
  function parseLocalDate(value) {
    var parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!parts) return null;
    return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }

  /**
   * The earliest date the shop will accept, as a plain YYYY-MM-DD string.
   *
   * Read it from the input's `min`, which Liquid rendered in the SHOP's timezone
   * (America/New_York) — never from the browser's clock. The shopper may be anywhere: someone in
   * Auckland ordering flowers for a relative in Florida is already on "tomorrow" locally while New
   * York is still on today, and a browser-side `new Date()` would reject a date the shop considers
   * perfectly valid. Comparing YYYY-MM-DD strings lexicographically is exact and needs no timezone
   * maths at all.
   */
  function earliestAllowed(field) {
    return field.getAttribute('min') || '';
  }

  function errorNodeFor(field) {
    var wrap = field.closest('.bff-df__field');
    return wrap ? wrap.querySelector('[data-bff-error]') : null;
  }

  function setError(field, message) {
    field.classList.add('bff-df__input--invalid');
    field.setAttribute('aria-invalid', 'true');
    var node = errorNodeFor(field);
    if (node) {
      node.textContent = message;
      node.hidden = false;
    }
  }

  function clearError(field) {
    field.classList.remove('bff-df__input--invalid');
    field.removeAttribute('aria-invalid');
    var node = errorNodeFor(field);
    if (node) {
      node.textContent = '';
      node.hidden = true;
    }
  }

  /** Validate one date field against the shop's delivery rules. */
  function validateDateField(field) {
    var value = (field.value || '').trim();
    if (!value) return true; // emptiness is the required-check's job

    var date = parseLocalDate(value);
    if (!date) {
      setError(field, PAST_MESSAGE);
      return false;
    }

    // String compare against the shop-timezone `min`; both are YYYY-MM-DD, so this is exact.
    var min = earliestAllowed(field);
    if (min && value < min) {
      setError(field, PAST_MESSAGE);
      return false;
    }

    // Timezone-independent: the date was built from its own Y/M/D parts, so getDay() is the weekday
    // of that calendar date no matter where the shopper is.
    if (date.getDay() === SUNDAY) {
      setError(field, SUNDAY_MESSAGE);
      return false;
    }

    clearError(field);
    return true;
  }

  /**
   * Validate every field in a container.
   * @returns {HTMLElement|null} the first invalid field, or null when the container is valid.
   */
  function validateContainer(container) {
    if (!container) return null;
    var firstInvalid = null;

    container.querySelectorAll('[data-bff-required]').forEach(function (field) {
      if (field.disabled) return;
      if (!(field.value || '').trim()) {
        setError(field, REQUIRED_MESSAGE);
        if (!firstInvalid) firstInvalid = field;
      } else {
        clearError(field);
      }
    });

    container.querySelectorAll('[data-bff-date]').forEach(function (field) {
      if (field.disabled) return;
      if (!(field.value || '').trim()) return; // already flagged by the required pass
      if (!validateDateField(field) && !firstInvalid) firstInvalid = field;
    });

    return firstInvalid;
  }

  function productContainer() {
    return document.querySelector('[data-bff-delivery-fields][data-bff-context="product"]');
  }

  function cartContainer() {
    return document.querySelector('[data-bff-delivery-fields][data-bff-context="cart"]');
  }

  function reject(firstInvalid) {
    firstInvalid.focus({ preventScroll: true });
    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Which container guards this form?
   * A /cart/add form is guarded by the product fields when they exist on the page. When they do not
   * (quick view, an app's own form), we let it through on purpose — the cart gate is the backstop.
   */
  function containerForForm(form) {
    if (!form || typeof form.getAttribute !== 'function') return null;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') !== -1) return productContainer();
    if (/\/cart(\?|$)/.test(action)) return cartContainer();
    return null;
  }

  /**
   * Keep empty optional fields off the order.
   * A blank textarea still posts as "Card message": "", which litters the florist's order view with
   * empty rows. Disabled controls are excluded from FormData, so we disable the empty optional ones
   * for the duration of this submit and re-enable them on the next task. The theme builds its
   * FormData synchronously inside its own submit handler, which runs after this one, so it sees them
   * disabled; the shopper never sees a greyed-out field.
   */
  function dropEmptyOptionalFields(container) {
    var dropped = [];
    container.querySelectorAll('[name]').forEach(function (field) {
      if (field.hasAttribute('data-bff-required')) return;
      if ((field.value || '').trim()) return;
      field.disabled = true;
      dropped.push(field);
    });
    if (!dropped.length) return;
    setTimeout(function () {
      dropped.forEach(function (field) {
        if (!field.hasAttribute('data-bff-mode-disabled')) field.disabled = false;
      });
    }, 0);
  }

  // Guard the actual Add to cart click as well. Some theme add buttons launch their own AJAX
  // flow before a normal submit handler can reject missing fields, especially the sticky bar.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var button = target.closest('button[type="submit"][name="add"], input[type="submit"][name="add"]');
      if (!button) return;
      var form = button.form || button.closest('form');
      var container = containerForForm(form);
      if (!container) return;
      syncOrderModeField(container, orderModeIsPickup(container));
      var firstInvalid = validateContainer(container);
      if (!firstInvalid) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      reject(firstInvalid);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var container = containerForForm(event.target);
      if (!container) return;
      syncOrderModeField(container, orderModeIsPickup(container));
      var firstInvalid = validateContainer(container);
      if (firstInvalid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        reject(firstInvalid);
        return;
      }
      dropEmptyOptionalFields(container);
    },
    true
  );

  /**
   * Accelerated checkout buttons (Shop Pay / PayPal / Google Pay) never fire a submit event —
   * they hand off straight to Shopify. Guard them on click, in the capture phase.
   */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var onProduct = target.closest('.m-product-dynamic-checkout');
      var onCart = target.closest('.additional-checkout-buttons');
      if (!onProduct && !onCart) return;

      var container = onProduct ? productContainer() : cartContainer();
      syncOrderModeField(container, orderModeIsPickup(container));
      var firstInvalid = validateContainer(container);
      if (!firstInvalid) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      reject(firstInvalid);
    },
    true
  );

  /** Live feedback: clear/repair the message as the shopper types or picks a date. */
  document.addEventListener('change', function (event) {
    var field = event.target;
    if (!field || typeof field.matches !== 'function') return;
    if (field.matches('[data-bff-date]')) {
      validateDateField(field);
      return;
    }
    if (field.matches('[data-bff-required]') && (field.value || '').trim()) clearError(field);
  });

  document.addEventListener('input', function (event) {
    var field = event.target;
    if (!field || typeof field.matches !== 'function') return;
    if (field.matches('[data-bff-required]') && (field.value || '').trim()) {
      if (!field.matches('[data-bff-date]')) clearError(field);
    }
  });

  /* ---- Delivery / pickup choice ----------------------------------------------- */
  var MODE_DELIVERY = 'Send Flowers';
  var MODE_PICKUP = 'Pickup at store';

  function orderModeIsPickup(container) {
    var pickupButton = container && container.querySelector('[data-bff-mode-button="pickup"]');
    return Boolean(pickupButton && pickupButton.getAttribute('aria-pressed') === 'true');
  }

  function syncOrderModeField(container, pickup) {
    if (!container) return;
    var modeInput = container.querySelector('[data-bff-mode-field]');
    if (!modeInput) return;
    var modeValue = pickup ? MODE_PICKUP : (container.getAttribute('data-bff-delivery-label') || MODE_DELIVERY);
    modeInput.value = modeValue;
    modeInput.setAttribute('value', modeValue);
  }

  function modeWrap(field) {
    return field && field.closest ? field.closest('.bff-df__field') : null;
  }

  function rememberHtml(node) {
    if (!node) return;
    if (!node.hasAttribute('data-bff-original-html')) {
      node.setAttribute('data-bff-original-html', node.innerHTML);
    }
  }

  function restoreHtml(node) {
    if (!node) return;
    var original = node.getAttribute('data-bff-original-html');
    if (original !== null) node.innerHTML = original;
  }

  function setModeHidden(field, hidden) {
    if (!field) return;
    var wrap = modeWrap(field);
    if (wrap) wrap.hidden = hidden;
    if (hidden) {
      field.setAttribute('data-bff-mode-disabled', '');
      field.disabled = true;
      clearError(field);
    } else {
      field.removeAttribute('data-bff-mode-disabled');
      field.disabled = false;
    }
  }

  function setModeFieldName(field, prefix, key) {
    if (field) field.name = prefix + '[' + key + ']';
  }

  function setOrderMode(container, pickup) {
    if (!container) return;

    syncOrderModeField(container, pickup);

    container.querySelectorAll('[data-bff-mode-button]').forEach(function (button) {
      var active = pickup ? button.getAttribute('data-bff-mode-button') === 'pickup' : button.getAttribute('data-bff-mode-button') === 'delivery';
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    var sender = container.querySelector('.bff-df__field--sender input');
    var phone = container.querySelector('.bff-df__field--phone input');
    var recipient = container.querySelector('.bff-df__field--recipient input');
    var card = container.querySelector('.bff-df__field--card textarea');
    var date = container.querySelector('.bff-df__field--date input');
    var time = container.querySelector('.bff-df__field--time select');
    var notes = container.querySelector('.bff-df__field--notes textarea');
    var fieldPrefix = container.getAttribute('data-bff-context') === 'cart' ? 'attributes' : 'properties';

    var heading = container.querySelector('.bff-df__heading');
    var senderWrap = modeWrap(sender);
    var phoneWrap = modeWrap(phone);
    var dateWrap = modeWrap(date);
    var timeWrap = modeWrap(time);
    var notesWrap = modeWrap(notes);
    var senderLabel = senderWrap && senderWrap.querySelector('.bff-df__label');
    var senderHelp = senderWrap && senderWrap.querySelector('.bff-df__help');
    var phoneLabel = phoneWrap && phoneWrap.querySelector('.bff-df__label');
    var phoneHelp = phoneWrap && phoneWrap.querySelector('.bff-df__help');
    var dateLabel = dateWrap && dateWrap.querySelector('.bff-df__label');
    var dateHelp = dateWrap && dateWrap.querySelector('.bff-df__help');
    var timeLabel = timeWrap && timeWrap.querySelector('.bff-df__label');
    var timeHelp = timeWrap && timeWrap.querySelector('.bff-df__help');
    var notesLabel = notesWrap && notesWrap.querySelector('.bff-df__label');
    var notesHelp = notesWrap && notesWrap.querySelector('.bff-df__help');
    var optionalSummary = container.querySelector('.bff-df__optional > summary');

    [heading, senderLabel, senderHelp, phoneLabel, phoneHelp, dateLabel, dateHelp, timeLabel, timeHelp, notesLabel, notesHelp, optionalSummary].forEach(rememberHtml);

    setModeHidden(recipient, pickup);
    setModeHidden(card, pickup);

    if (recipient) {
      if (pickup) {
        recipient.removeAttribute('required');
        recipient.removeAttribute('data-bff-required');
      } else {
        recipient.setAttribute('required', '');
        recipient.setAttribute('data-bff-required', '');
      }
    }

    if (pickup) {
      setModeFieldName(sender, fieldPrefix, 'Customer Name');
      setModeFieldName(phone, fieldPrefix, 'Customer phone number');
      setModeFieldName(date, fieldPrefix, 'Pickup date');
      setModeFieldName(time, fieldPrefix, 'Pickup time');
      setModeFieldName(notes, fieldPrefix, 'Additional information');

      if (heading) heading.textContent = 'Choose your pickup details.';
      if (senderLabel) senderLabel.innerHTML = 'Customer name<sup class="bff-df__req" aria-hidden="true">*</sup>';
      if (senderHelp) senderHelp.textContent = 'Name of the person placing or picking up the order.';
      if (phoneLabel) phoneLabel.innerHTML = 'Customer phone number<sup class="bff-df__req" aria-hidden="true">*</sup>';
      if (phoneHelp) phoneHelp.textContent = 'We may contact you if we have questions or when your pickup order is ready.';
      if (dateLabel) dateLabel.innerHTML = 'Pickup date<sup class="bff-df__req" aria-hidden="true">*</sup>';
      if (dateHelp) dateHelp.textContent = 'Choose the day you plan to pick up your order in store.';
      if (timeLabel) timeLabel.innerHTML = 'Pickup time<sup class="bff-df__req" aria-hidden="true">*</sup>';
      if (timeHelp) timeHelp.textContent = 'Choose Morning or Afternoon so we know when to have your order ready.';
      if (notesLabel) notesLabel.textContent = 'Additional information';
      if (notesHelp) notesHelp.textContent = 'Add anything else we should know while preparing your pickup order.';
      if (optionalSummary) optionalSummary.textContent = 'Add additional information (optional)';

      if (time) {
        time.setAttribute('required', '');
        time.setAttribute('data-bff-required', '');
        var anytime = time.querySelector('option[value="Anytime"]');
        if (anytime) anytime.disabled = true;
        if (time.value === 'Anytime') time.value = '';
      }
    } else {
      setModeFieldName(sender, fieldPrefix, 'Sender Name');
      setModeFieldName(phone, fieldPrefix, 'Phone number');
      setModeFieldName(date, fieldPrefix, 'Preferred delivery date');
      setModeFieldName(time, fieldPrefix, 'Preferred delivery time');
      setModeFieldName(notes, fieldPrefix, 'Special delivery instructions');

      restoreHtml(heading);
      restoreHtml(senderLabel);
      restoreHtml(senderHelp);
      restoreHtml(phoneLabel);
      restoreHtml(phoneHelp);
      restoreHtml(dateLabel);
      restoreHtml(dateHelp);
      restoreHtml(timeLabel);
      restoreHtml(timeHelp);
      restoreHtml(notesLabel);
      restoreHtml(notesHelp);
      restoreHtml(optionalSummary);

      if (time) {
        time.removeAttribute('required');
        time.removeAttribute('data-bff-required');
        var anytimeOption = time.querySelector('option[value="Anytime"]');
        if (anytimeOption) anytimeOption.disabled = false;
        if (!time.value) time.value = 'Anytime';
        clearError(time);
      }
    }

    var productArea = container.closest && container.closest('.m-main-product');
    var zip = (productArea && productArea.querySelector('[data-bff-zip-check]')) || document.querySelector('[data-bff-zip-check]');
    if (zip) zip.hidden = pickup;
  }

  function initOrderModes(scope) {
    (scope || document).querySelectorAll('[data-bff-delivery-fields]').forEach(function (container) {
      var modeInput = container.querySelector('[data-bff-mode-field]');
      var pickup = modeInput && modeInput.value === MODE_PICKUP;
      setOrderMode(container, pickup);
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest && event.target.closest('[data-bff-mode-button]');
    if (!button) return;
    var container = button.closest('[data-bff-delivery-fields]');
    setOrderMode(container, button.getAttribute('data-bff-mode-button') === 'pickup');
  });


  function setReuseMode(container, reuse) {
    if (!container) return;
    var box = container.querySelector('[data-bff-reuse-box]');
    if (!box) return;
    container.querySelectorAll('.bff-df__field').forEach(function (wrap) {
      wrap.hidden = reuse;
      wrap.querySelectorAll('input, select, textarea').forEach(function (field) {
        if (reuse) {
          field.removeAttribute('data-bff-reuse-disabled');
          field.disabled = false;
        } else if (field.hasAttribute('data-bff-mode-disabled')) {
          field.disabled = true;
        }
      });
    });
    var heading = container.querySelector('.bff-df__heading');
    if (heading) heading.hidden = reuse;
  }

  document.addEventListener('change', function (event) {
    var toggle = event.target;
    if (!toggle || !toggle.matches || !toggle.matches('[data-bff-reuse-toggle]')) return;
    var container = toggle.closest('[data-bff-delivery-fields]');
    setReuseMode(container, toggle.checked);
  });

  function initReuse(scope) {
    (scope || document).querySelectorAll('[data-bff-reuse-toggle]').forEach(function (toggle) {
      setReuseMode(toggle.closest('[data-bff-delivery-fields]'), toggle.checked);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initOrderModes(document); initReuse(document); });
  } else {
    initOrderModes(document);
    initReuse(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    initOrderModes(event.target || document);
    initReuse(event.target || document);
  });

})();
